import {
  DOWN_PAYMENT_SYNC_SOURCE,
  DownPaymentFieldName,
} from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/MainContent/LoanInfo/LoanInfo.constants';
import {
  getDownPaymentSyncSource,
  hasFieldChanged,
  resolvePurchaseDownPaymentSync,
} from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/MainContent/LoanInfo/LoanInfo.utils';

import { governmentRefinancePurposeOptions } from 'constants/options';
import {
  checkIsGovernmentRefinancePurposeVisible,
  checkIsPurchaseFlow,
  getIsAllowedGovernmentRefinancePurposeValue,
} from 'utils/loanApplication';

import {
  BASE_LOAN_AMOUNT_FIELD_NAME,
  DOWN_PAYMENT_FIELD_NAME,
  GOVERNMENT_REFINANCE_DEFAULT_LOAN_TYPES,
  GOVERNMENT_REFINANCE_PURPOSE_FIELD_NAME,
} from './LoanEditorCustomFormDrawer.constants';

export const withDerivedDownPayment = ({ applicationData, purchasePrice }) => {
  if (!checkIsPurchaseFlow(applicationData?.loanPurpose) || !Number(purchasePrice)) {
    return applicationData;
  }

  const { downPaymentValue, downPaymentPercent } = resolvePurchaseDownPaymentSync({
    source: DOWN_PAYMENT_SYNC_SOURCE.LOAN,
    purchasePrice,
    downPaymentValue: applicationData[DownPaymentFieldName.AMOUNT],
    downPaymentPercent: null,
    baseLoanAmount: applicationData[BASE_LOAN_AMOUNT_FIELD_NAME],
  });

  return {
    ...applicationData,
    [DownPaymentFieldName.AMOUNT]: downPaymentValue,
    [DownPaymentFieldName.PERCENT]: downPaymentPercent,
  };
};

export const pickDownPaymentSyncValues = formState => ({
  downPaymentValue: formState?.[DownPaymentFieldName.AMOUNT],
  downPaymentPercent: formState?.[DownPaymentFieldName.PERCENT],
  baseLoanAmount: formState?.[BASE_LOAN_AMOUNT_FIELD_NAME],
});

const getConditionalUpdate = ({ currentValues, nextValues, key }) =>
  nextValues[key] === currentValues[key] ? {} : { [key]: nextValues[key] };

export const buildDownPaymentSyncUpdates = ({ previousValues, currentValues, purchasePrice }) => {
  const hasDownPaymentValueChanged = hasFieldChanged(
    previousValues.downPaymentValue,
    currentValues.downPaymentValue,
  );
  const hasDownPaymentPercentChanged = hasFieldChanged(
    previousValues.downPaymentPercent,
    currentValues.downPaymentPercent,
  );
  const hasLoanAmountChanged = hasFieldChanged(
    previousValues.baseLoanAmount,
    currentValues.baseLoanAmount,
  );

  if (!hasDownPaymentValueChanged && !hasDownPaymentPercentChanged && !hasLoanAmountChanged) {
    return {};
  }

  const nextValues = resolvePurchaseDownPaymentSync({
    source: getDownPaymentSyncSource({ hasDownPaymentPercentChanged, hasDownPaymentValueChanged }),
    purchasePrice,
    ...currentValues,
  });

  return {
    ...getConditionalUpdate({ currentValues, nextValues, key: 'downPaymentValue' }),
    ...getConditionalUpdate({ currentValues, nextValues, key: 'downPaymentPercent' }),
    ...getConditionalUpdate({ currentValues, nextValues, key: 'baseLoanAmount' }),
  };
};

export const checkIsDownPaymentPlaced = placedFieldPaths =>
  !!placedFieldPaths?.has(DownPaymentFieldName.AMOUNT);

export const getDownPaymentCascadeFieldPaths = fieldName =>
  fieldName === DOWN_PAYMENT_FIELD_NAME ? [BASE_LOAN_AMOUNT_FIELD_NAME] : [];

export const getGovernmentRefinancePurposeDefaultOption = ({
  placedFieldPaths,
  mortgageType,
  loanPurpose,
  governmentRefinancePurpose,
}) => {
  const isApplicable =
    !!placedFieldPaths?.has(GOVERNMENT_REFINANCE_PURPOSE_FIELD_NAME) &&
    checkIsGovernmentRefinancePurposeVisible(mortgageType, loanPurpose) &&
    GOVERNMENT_REFINANCE_DEFAULT_LOAN_TYPES.has(mortgageType);

  if (
    !isApplicable ||
    getIsAllowedGovernmentRefinancePurposeValue(mortgageType, governmentRefinancePurpose)
  ) {
    return null;
  }

  return governmentRefinancePurposeOptions[0];
};
